import { useEffect, useRef } from "react";
import { holdLoader, isReady } from "../../boot";

// Letterforms with big sweeping strokes in Aref Ruqaa, mostly single letters
// and short clusters so they read as shapes, like a calligraphy wall.
const glyphs = ["ع", "ح", "ك", "ل", "ن", "هـ", "و", "ي", "ص", "س", "ق", "م", "لا", "ب", "ط", "ر", "عـ", "حـ", "كـ", "فـ", "ى", "ج"];

// Deterministic randomness so the composition is identical on every visit.
const rng = (seed) => () => {
	seed |= 0;
	seed = (seed + 0x6d2b79f5) | 0;
	let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
	t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
	return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const scatter = ({ count, seed, size, edgesOnly }) => {
	const rand = rng(seed);
	const cols = Math.ceil(Math.sqrt(count * 1.4));
	const rows = Math.ceil(count / cols);
	return Array.from({ length: count }, (_, i) => {
		// Jittered grid keeps coverage even while still feeling random.
		let x = ((i % cols) + rand()) / cols;
		const y = (Math.floor(i / cols) + rand()) / rows;
		if (edgesOnly) x = rand() < 0.5 ? rand() * 0.22 : 0.78 + rand() * 0.22;
		const r0 = (rand() - 0.5) * 70;
		return {
			glyph: glyphs[Math.floor(rand() * glyphs.length)],
			x: x * 1.1 - 0.12,
			y: y - 0.06,
			rem: size[0] + rand() * (size[1] - size[0]),
			r0: (r0 * Math.PI) / 180,
			r1: ((r0 + (rand() - 0.5) * 30) * Math.PI) / 180,
			dx: (rand() - 0.5) * 160,
			dy: (rand() - 0.5) * 140,
			period: 38 + rand() * 40,
			phase: rand() * 60,
			flip: rand() < 0.3,
			opacity: 0.55 + rand() * 0.45,
		};
	});
};

const BACK = { pieces: scatter({ count: 34, seed: 7, size: [9, 22] }), top: -0.1, height: 1.5, travel: 0.14 };
const FRONT = { pieces: scatter({ count: 9, seed: 21, size: [18, 32], edgesOnly: true }), top: -0.1, height: 1.7, travel: 0.34 };

// Large soft blotches times fine speckle, thresholded: worn gold leaf.
const GRUNGE_SVG =
	"data:image/svg+xml;utf8," +
	encodeURIComponent(
		"<svg xmlns='http://www.w3.org/2000/svg' width='520' height='520'><filter id='g' x='0' y='0'><feTurbulence type='fractalNoise' baseFrequency='0.018' numOctaves='3' seed='4' stitchTiles='stitch' result='a'/><feTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='2' seed='9' stitchTiles='stitch' result='b'/><feComposite in='a' in2='b' operator='arithmetic' k2='0.65' k3='0.55' result='c'/><feColorMatrix in='c' values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  3.2 0 0 0 -0.8'/><feComponentTransfer><feFuncA type='table' tableValues='0 0.7 1 1'/></feComponentTransfer></filter><rect width='100%' height='100%' filter='url(#g)'/></svg>"
	);

const loadImage = (src) =>
	new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => resolve(img);
		img.onerror = reject;
		img.src = src;
	});

/** Draws one letterform, in gold or olive, with the worn texture baked in. */
const makeSprite = (piece, front, grunge, scale, fit) => {
	const px = piece.rem * 16 * scale * fit;
	const font = `700 ${px}px "Aref Ruqaa", serif`;
	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d");
	ctx.font = font;
	const m = ctx.measureText(piece.glyph);
	const pad = px * 0.1;
	const w = Math.max(1, Math.ceil(m.actualBoundingBoxLeft + m.actualBoundingBoxRight + pad * 2));
	const h = Math.max(1, Math.ceil(m.actualBoundingBoxAscent + m.actualBoundingBoxDescent + pad * 2));
	canvas.width = w;
	canvas.height = h;
	ctx.font = font; // resizing the canvas resets its state
	if (piece.flip) {
		ctx.translate(w, 0);
		ctx.scale(-1, 1);
	}
	if (front) {
		const g = ctx.createLinearGradient(0, 0, w, h);
		g.addColorStop(0, "#9a7a34");
		g.addColorStop(0.4, "#f8e7b0");
		g.addColorStop(0.6, "#d4af5f");
		g.addColorStop(1, "#8f6f2c");
		ctx.fillStyle = g;
	} else {
		ctx.fillStyle = "rgba(111, 90, 38, 0.5)";
	}
	ctx.fillText(piece.glyph, pad + m.actualBoundingBoxLeft, pad + m.actualBoundingBoxAscent);

	if (grunge) {
		ctx.setTransform(1, 0, 0, 1, 0, 0);
		ctx.globalCompositeOperation = "destination-in";
		ctx.fillStyle = ctx.createPattern(grunge, "repeat");
		ctx.fillRect(0, 0, w, h);
	}
	return { canvas, scale };
};

const ease = (t) => (1 - Math.cos(t * Math.PI * 2)) / 2; // 0 -> 1 -> 0, eased like the old keyframes

// Run work in small slices when the browser is idle, so it never blocks input.
const idle = (fn) =>
	"requestIdleCallback" in window
		? window.requestIdleCallback(fn, { timeout: 500 })
		: setTimeout(() => fn({ timeRemaining: () => 12 }), 16);

/**
 * Fixed, page-wide wall of moving calligraphy in two depths, drawn on a single
 * canvas from pre-rendered sprites so each frame is just a few dozen image blits.
 */
const Background = () => {
	const canvasRef = useRef(null);

	useEffect(() => {
		const canvas = canvasRef.current;
		const ctx = canvas.getContext("2d");
		const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
		// Fewer pieces on small screens: same density, less work.
		const area = Math.min(1, (window.innerWidth * window.innerHeight) / (1440 * 900));
		// Letter size and drift follow the screen width, so a phone gets the same
		// composition in miniature instead of a few letters as wide as the screen.
		const fit = Math.min(1, Math.max(0.45, window.innerWidth / 1280));
		const layers = [
			{
				...BACK,
				pieces: BACK.pieces.slice(0, Math.max(12, Math.round(BACK.pieces.length * area))),
				front: false,
				scale: 1, // the dim back layer does not need high-density pixels
			},
			{
				...FRONT,
				pieces: FRONT.pieces.slice(0, Math.max(5, Math.round(FRONT.pieces.length * area))),
				front: true,
				scale: dpr,
			},
		];
		let width = 0;
		let height = 0;
		let scrollMax = 1;
		let raf = 0;
		let alive = true;
		let lastDraw = 0;
		let lastScroll = -1;
		// Cached from scroll events: reading scrollY inside the frame loop can force
		// a synchronous layout when another animation has just written styles.
		let scrollY = window.scrollY;
		const onScroll = () => {
			scrollY = window.scrollY;
		};
		let built = false;
		let shownAt = 0; // when the page is revealed; the wall fades in as one
		let markBuilt;
		holdLoader(new Promise((resolve) => (markBuilt = resolve)));

		// Pointer parallax for the front layer, smoothed like a spring.
		const pointer = { x: 0, y: 0, sx: 0, sy: 0 };
		const onMove = (e) => {
			pointer.x = (e.clientX / width - 0.5) * -40;
			pointer.y = (e.clientY / height - 0.5) * -30;
		};

		// Layout is read only here (on resize), never inside the frame loop. The
		// wall is sized to the large viewport (100lvh), which stays put while a
		// phone's address bar slides in and out, so scrolling never re-lays it out.
		const measure = () => {
			width = canvas.clientWidth;
			height = canvas.clientHeight;
			scrollMax = Math.max(1, document.documentElement.scrollHeight - height);
		};
		const resize = () => {
			measure();
			const w = Math.round(width * dpr);
			const h = Math.round(height * dpr);
			// Assigning a canvas size clears it, even to the same value: skip no-ops.
			if (canvas.width !== w) canvas.width = w;
			if (canvas.height !== h) canvas.height = h;
		};

		const drawLayer = (layer, now, progress, alpha, offsetX, offsetY, extraRotate) => {
			const top = (layer.top - layer.travel * layer.height * progress) * height + offsetY;
			const layerH = layer.height * height;
			const cx = width / 2;
			const cy = top + layerH / 2;
			ctx.save();
			ctx.translate(cx * dpr, cy * dpr);
			if (extraRotate) ctx.rotate(extraRotate);
			ctx.translate(-cx * dpr, -cy * dpr);
			layer.pieces.forEach((p, i) => {
				const sprite = layer.sprites?.[i];
				if (!sprite) return;
				const k = dpr / sprite.scale; // draw low-res sprites at full size
				const sw = sprite.canvas.width * k;
				const sh = sprite.canvas.height * k;
				const t = reduce ? 0 : ease(((now / 1000 + p.phase) % p.period) / p.period);
				const x = (p.x * width + p.dx * fit * t + offsetX) * dpr;
				const y = (top + p.y * layerH + p.dy * fit * t) * dpr;
				if (y > height * dpr + sh || y < -sh * 1.5) return;
				ctx.save();
				ctx.globalAlpha = p.opacity * alpha;
				ctx.translate(x + sw / 2, y + sh / 2);
				ctx.rotate(p.r0 + (p.r1 - p.r0) * t);
				ctx.drawImage(sprite.canvas, -sw / 2, -sh / 2, sw, sh);
				ctx.restore();
			});
			ctx.restore();
		};

		const paint = (now) => {
			if (!shownAt) {
				// Reduced motion has no fade, so it may draw under the loader.
				if (!built || (!reduce && !isReady())) return;
				shownAt = now;
			}
			const intro = reduce ? 1 : Math.min(1, (now - shownAt) / 2000);
			if (intro < 1 || canvas.style.opacity !== "1") canvas.style.opacity = String(1 - Math.pow(1 - intro, 3));
			const progress = Math.min(1, scrollY / scrollMax);
			pointer.sx += (pointer.x - pointer.sx) * 0.08;
			pointer.sy += (pointer.y - pointer.sy) * 0.08;
			ctx.clearRect(0, 0, canvas.width, canvas.height);
			drawLayer(layers[0], now, progress, 1, 0, 0, (-6 * progress * Math.PI) / 180);
			drawLayer(layers[1], now, progress, width < 768 ? 0.5 : 1, pointer.sx, pointer.sy, 0);
		};

		const frame = (now) => {
			if (!alive) return;
			raf = requestAnimationFrame(frame);
			const scrolled = scrollY !== lastScroll;
			const settling = Math.abs(pointer.x - pointer.sx) + Math.abs(pointer.y - pointer.sy) > 0.5;
			// The drift is slow: 30fps looks the same unless the page itself is moving.
			if (!scrolled && !settling && now - lastDraw < 32) return;
			lastDraw = now;
			lastScroll = scrollY;
			paint(now);
		};

		// Reduced motion: a still composition, repainted only when something changes.
		const drawOnce = () => requestAnimationFrame(paint);

		const onResize = () => {
			resize();
			if (reduce) drawOnce();
		};
		resize();
		window.addEventListener("resize", onResize);
		const ro = new ResizeObserver(measure);
		ro.observe(document.body);
		if (!reduce) window.addEventListener("pointermove", onMove, { passive: true });
		window.addEventListener("scroll", onScroll, { passive: true });
		if (reduce) window.addEventListener("scroll", drawOnce, { passive: true });

		// Build sprites in slices: bigger ones while the loader is up (its animation
		// runs on the compositor), small idle ones afterwards so input never waits.
		const build = (grunge) => {
			const queue = layers.flatMap((layer) => {
				layer.sprites = [];
				return layer.pieces.map((p, i) => ({ layer, p, i }));
			});
			const step = (deadline) => {
				if (!alive) return;
				const until = performance.now() + (isReady() ? 0 : 40);
				do {
					const job = queue.shift();
					if (!job) break;
					job.layer.sprites[job.i] = makeSprite(job.p, job.layer.front, grunge, job.layer.scale, fit);
				} while (queue.length && (performance.now() < until || deadline.timeRemaining() > 8));
				if (queue.length) next();
				else {
					built = true;
					markBuilt();
					if (reduce) drawOnce();
				}
			};
			const next = () => (isReady() ? idle(step) : setTimeout(() => step({ timeRemaining: () => 0 }), 0));
			next();
			if (!reduce) raf = requestAnimationFrame(frame);
		};
		// Start right away: the loader is covering the page while this runs.
		Promise.all([
			document.fonts.load('700 100px "Aref Ruqaa"', "عحك"),
			loadImage(GRUNGE_SVG).catch(() => null),
		])
			.then(([, grunge]) => alive && build(grunge))
			.catch(markBuilt);

		return () => {
			alive = false;
			markBuilt(); // never leave the loader waiting on a torn-down instance
			cancelAnimationFrame(raf);
			ro.disconnect();
			window.removeEventListener("resize", onResize);
			window.removeEventListener("pointermove", onMove);
			window.removeEventListener("scroll", onScroll);
			window.removeEventListener("scroll", drawOnce);
		};
	}, []);

	return (
		<div className="pointer-events-none fixed inset-x-0 top-0 -z-10 h-screen overflow-hidden supports-[height:100lvh]:h-lvh bg-ink" aria-hidden="true">
			<div className="absolute -top-1/4 left-1/2 h-[80vh] w-[80vw] -translate-x-1/2 rounded-full bg-gold/[0.07] blur-[120px]" />
			<div className="absolute -bottom-1/4 -left-1/4 h-[60vh] w-[60vw] rounded-full bg-crimson/[0.08] blur-[140px]" />
			<canvas ref={canvasRef} className="absolute inset-0 h-full w-full opacity-0" />
			{/* Keeps the reading column calm while the edges stay busy */}
			<div className="absolute inset-0 bg-[radial-gradient(ellipse_55%_65%_at_center,rgba(11,10,8,0.88)_0%,rgba(11,10,8,0.55)_55%,transparent_100%)]" />
			<div className="grain absolute inset-0 opacity-[0.045]" />
		</div>
	);
};

export default Background;
